import { useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { TableFiltering } from 'shared/components/table/hooks/table-filtering';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';

import ReadResidentFiltersResponse from 'features/residents/models/read-residents-filters-response';
import ReadResidentsQuery from 'features/residents/models/read-residents-query';
import Resident from 'features/residents/models/resident';
import ResidentSettingsFilter from 'features/residents/models/resident-settings-filter';
import { FilterValueType } from 'features/residents/models/resident-settings-filter-value';
import { useLazyReadResidentFilters } from 'features/residents/resident-settings/providers/resident-queries';

export type ResidentSettingFiltersState = {
  getResidentSettingsQuery: ReadResidentsQuery;
  activeFilters: ResidentSettingsFilter[];
  searchBar: string;
  organizationUnits: SelectedOrganisationTreeNode[];
  navigationParentOrganisationUnitId?: string;
  selectOptions: (filterValueType: FilterValueType, keys: string[]) => void;
  changeSearchBarValue: (searchBarValue: string) => void;
  searchBarConfirmationGuard: (changeLocalValue: () => void) => void;
  changeSelectedOrganizationUnits: (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) => void;
  clearFilters: () => void;
  applyNavigationFilters: (parentOrganisationUnit: OrganisationTreeNode, searchTerm: string) => void;
  residentFilters: ReadResidentFiltersResponse | undefined;
};

type Props = {
  table: TableFiltering<Resident>;
};

const useResidentSettingFiltersState = ({ table }: Readonly<Props>): ResidentSettingFiltersState => {
  const [activeFilters, setActiveFilters] = useSessionStorage<ResidentSettingsFilter[]>(
    FilterSessionStorageKeys.RESIDENT_SETTING_FILTERS,
    [],
  );
  const [organizationUnits, setOrganizationUnits] = useSessionStorage<SelectedOrganisationTreeNode[]>(
    FilterSessionStorageKeys.RESIDENT_SETTING_ORG_UNITS,
    [],
  );
  const [searchBar, setSearchBar] = useSessionStorage<string>(FilterSessionStorageKeys.RESIDENT_SETTING_SEARCH_BAR, '');
  const [navigationParentOrganisationUnitId, setNavigationParentOrganisationUnitId] = useState<string>();

  const { refetch: getResidentsFilters, data: residentFilters } = useLazyReadResidentFilters({
    organisationUnitIds: organizationUnits.map((x) => x.id),
  });

  const [getResidentSettingsQuery, setGetResidentSettingsQuery] = useState<ReadResidentsQuery>({
    sort: table.sortFromSessionStorage ?? { isAscending: true, property: 'organisationUnitName' },
    pagination: table.currentPagination[0],
    filters: activeFilters,
    searchQuery: searchBar,
  } as ReadResidentsQuery);

  const changeSearchBarValue = (searchBarValue: string): void => {
    setSearchBar(searchBarValue);
    table.resetPaging();
  };

  const changeFilters = (filterValueType: FilterValueType, keys: string[] | string): void => {
    const newFilter = {
      filterValueType: filterValueType,
      values: Array.isArray(keys) ? keys : [keys],
    };
    const updatedFiltersList = [...getResidentSettingsQuery.filters];

    if (updatedFiltersList.findIndex((x) => x.filterValueType === filterValueType) !== -1) {
      updatedFiltersList[updatedFiltersList.findIndex((x) => x.filterValueType === filterValueType)] = newFilter;
    } else {
      updatedFiltersList.push(newFilter);
    }

    setActiveFilters(updatedFiltersList.filter((x) => x.values.length > 0));

    table.resetPaging();
  };

  const selectOptions = (filterValueType: FilterValueType, keys: string[] | string): void => changeFilters(filterValueType, keys);

  const changeSelectedOrganizationUnits = (selectedOrganisationUnits: SelectedOrganisationTreeNode[]): void => {
    setNavigationParentOrganisationUnitId(undefined);
    setOrganizationUnits(selectedOrganisationUnits);
    changeFilters(
      FilterValueType.OrganisationUnit,
      selectedOrganisationUnits.map((x) => x.id),
    );
  };

  const clearFilters = (): void => {
    setNavigationParentOrganisationUnitId(undefined);
    setActiveFilters([]);
    setOrganizationUnits([]);
    setSearchBar('');
    setGetResidentSettingsQuery((prevState) => ({
      ...prevState,
      filters: [],
    }));

    table.resetPaging();
  };

  const applyNavigationFilters = (parentOrganisationUnit: OrganisationTreeNode, searchTerm: string): void => {
    const selectOrganisationUnitAndDescendants = (organisationUnit: OrganisationTreeNode): SelectedOrganisationTreeNode[] => [
      {
        id: organisationUnit.id,
        name: organisationUnit.name,
        depth: organisationUnit.depth,
      },
      ...organisationUnit.childOrganisationUnits.flatMap(selectOrganisationUnitAndDescendants),
    ];
    const selectedOrganisationUnits = selectOrganisationUnitAndDescendants(parentOrganisationUnit);
    const organisationUnitFilter: ResidentSettingsFilter = {
      filterValueType: FilterValueType.OrganisationUnit,
      values: selectedOrganisationUnits.map((organisationUnit) => organisationUnit.id),
    };

    setActiveFilters([organisationUnitFilter]);
    setOrganizationUnits(selectedOrganisationUnits);
    setSearchBar(searchTerm);
    setNavigationParentOrganisationUnitId(parentOrganisationUnit.id);
    table.resetPaging();
  };

  const buildAndSaveQuery = (): void => {
    if (
      table.currentPagination[0].skip !== getResidentSettingsQuery.pagination.skip ||
      table.currentPagination[0].take !== getResidentSettingsQuery.pagination.take ||
      activeFilters !== getResidentSettingsQuery.filters ||
      searchBar !== getResidentSettingsQuery.searchQuery
    ) {
      setGetResidentSettingsQuery((prevState) => ({
        ...prevState,
        pagination: table.currentPagination[0],
        filters: activeFilters,
        searchQuery: searchBar,
      }));
    }
  };

  useEffect(() => {
    buildAndSaveQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.currentPage, table.currentRowsPerPage, activeFilters, searchBar]);

  useEffect(() => {
    getResidentsFilters();
  }, [organizationUnits]);

  return {
    getResidentSettingsQuery,
    activeFilters,
    searchBar,
    organizationUnits,
    navigationParentOrganisationUnitId,
    selectOptions,
    changeSearchBarValue,
    searchBarConfirmationGuard: {} as never,
    changeSelectedOrganizationUnits,
    clearFilters,
    applyNavigationFilters,
    residentFilters,
  };
};

export default useResidentSettingFiltersState;
